import axios from 'axios';
import { React, useState, useContext } from 'react'
import Constants from '../../Constants';
import ConfirmPopup from '../../Utils/ConfirmPopup';
import { Dropdown, Spin } from 'antd';
import { UserContext } from '../../Contexts/UserContext';
import { LoadingOutlined } from '@ant-design/icons';
import { mostActiveAcquirerExportUrl } from '../../Config/config';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import { TableDataContext } from '../../Contexts/TableDataContext';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { authGenerateUrl, authVerifyUrl } from '../../Config/config';
import DownloadTrigger from '../ExportPopup/DownloadTrigger';

/**
 * The `MostActiveAcquirerDownloader` function is a component that renders a dropdown menu with options
 * for downloading Excel files for most active acquirers, and handles the logic for downloading the selected file.
 * @returns a JSX element.
 */
function MostActiveAcquirerDownloader({ menuItems }) {
    const [alert, setAlert] = useState({ show: false, message: Constants.excelPopupMessage, key: "", })
    const { getToken, logout, authAlert } = useContext(UserContext);
    const [authCode, setAuthCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [reachedLimitPopup, setReachedLimitPopup] = useState({ show: false, message: "" })
    const [isDownloading, setIsDownloading] = useState(false);
    const { monthPickerValue, refineSearchFilters } = useContext(PeFilterContext);
    const { tableAcquirerId, allDealIds } = useContext(TableDataContext);
    const location = useLocation();
    const filters = refineSearchFilters[location.pathname];

    const onChange = async (flag) => {
        if (authAlert() && flag === 1) {
            setLoading(true)

            try {
                const generateResponse = await axios.post(authGenerateUrl, {}, {
                    headers: {
                        "Authorization": getToken(),
                    },
                });

                if (generateResponse.status === 200) {
                    setReachedLimitPopup({
                        show: true,
                        message: "An authorization code has been sent to your registered email ID. Please enter it below to proceed with data export through the database.",
                    });
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error generating authorization code:", error);
                setLoading(false);
                toast.error("Failed to generate authorization code. Please try again.", { position: "top-center" });
            }
            return;
        }
        if (flag === 1) {
            const result = await downloadExcel(alert.key, alert.fileName)
            if (result === true) {
            } else {
                setReachedLimitPopup({ show: true, message: result })
            }
        }
        setAlert(prev => ({ ...prev, show: false, key: "" }))
    }

    const downloadExcel = async (key, fileName) => {
        try {
            setIsDownloading(true);

            const response = await axios.post(mostActiveAcquirerExportUrl, {
                "download_type": key,
                "with_transaction": key === "profile_with_inv" ? true : false,
                acquirer_id: tableAcquirerId,
                deal_ids: allDealIds,
                "date_range": {
                    "from_date": monthPickerValue[0],
                    "to_date": monthPickerValue[1]
                },
                acquirer_group: filters?.acquirer_group?.map(opt => opt.name),
                "acquirer_city": filters?.acquirerCity?.map(city => city.id),
                "acquirer_company_type": filters?.acquirerType?.map(type => type.id),
                "acquirer_country": filters?.acquirerCountries?.map(country => country.id),
                "company_group": filters?.group?.map(group => group.name),
                "acquirer_region": filters?.acquirerRegion?.map(region => region.id),
                "acquirer_state": filters?.acquirerState?.map(state => state.id),
                "deal_range": filters?.dealRange?.at(0) && {
                    "from": filters?.dealRange[0].name,
                    "to": filters?.dealRange[1].name
                },
                "deal_type": filters?.dealType?.map(type => type.id),
                "ladvisor_ids": filters?.ladvisors?.map(advisor => advisor.id),
                "tadvisor_ids": filters?.tadvisors?.map(advisor => advisor.id),
                "target_city": filters?.city?.map(city => city.id),
                "target_company_type": filters?.companyType?.map(type => type.id),
                "target_country": filters?.countries?.map(country => country.id),
                "target_industry": filters?.industry?.map(industry => industry.id),
                "acquirer_industry": filters?.acquirer_industry?.map(industry => industry.id),
                "target_sector": filters?.sector?.map(sector => sector.name),
                "target_region": filters?.region?.map(region => region.id),
                "target_state": filters?.state?.map(state => state.id),
                "valuation": filters?.valuations?.map(valuation => valuation.value)
            }, {
                headers: {
                    "Authorization": getToken(),
                    "Content-Type": "application/json",
                },
                responseType: 'blob',
            });

            const contentType = response.headers['content-type'];

            if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                const blob = new Blob([response.data], { type: contentType });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `${fileName}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                return true;
            } else {
                const text = await response.data.text();
                const data = JSON.parse(text);
                toast.success(data?.message, { position: "top-center" });
                return true;
            }
        } catch (error) {
            console.error("downloadExcel - error", error);
            if (error?.response?.status === Constants.sessionTerminatedStatus) {
                logout();
            } else if (error?.response?.status === 400) {
                const dataAsText = await error?.response?.data?.text();
                const parsedData = JSON.parse(dataAsText);
                return parsedData?.message || "Something went wrong during export";
            }
            return error?.response?.data?.message || "Something went wrong during export";
        } finally {
            setIsDownloading(false);
        }
    }

    const onItemSelect = (e) => {
        const selectedItem = menuItems.filter(item => item.key === e.key)?.at(0);
        setAlert(prev => ({ ...prev, show: true, key: e.key, fileName: selectedItem.fileName, }))
    }

    const handleAuthCodeSubmit = async (code) => {
        try {
            const verifyResponse = await axios.post(authVerifyUrl, { authCode: code }, {
                headers: {
                    "Authorization": getToken(),
                },
            });

            if (verifyResponse.status === 200) {
                toast.success(verifyResponse?.data?.message, { position: "top-center" });
                localStorage.setItem(Constants.localStorageKeys.exportExpireAt, verifyResponse?.data?.export_expires_at);

                setReachedLimitPopup({ show: false, message: "" });
                const result = await downloadExcel(alert.key, alert.fileName)
                if (result === true) {
                } else {
                    setReachedLimitPopup({ show: true, message: result })
                }
                setAlert(prev => ({ ...prev, show: false, key: "" }))
            }
        } catch (error) {
            console.error("Error verifying authorization code:", error.response.data.message);
            toast.error(error?.response?.data?.message, { position: "top-center" });
        }
    };

    if (reachedLimitPopup.show) {
        if (authAlert()) {
            return (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75 z-10">
                    <div className="relative w-full max-w-lg mx-4 md:mx-0 bg-white rounded-lg shadow-xl">
                        <div className="pt-5 px-5 flex justify-between items-center">
                            <h1 className="text-lg font-bold text-gray-900">Authorization Required</h1>
                            <button
                                onClick={() => setReachedLimitPopup({ show: false, message: "" })}
                                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="p-5">
                            <p className="text-sm text-gray-500">{reachedLimitPopup.message}</p>
                            <div className="mt-4 pl-4 flex items-center gap-2">
                                <input
                                    type="text"
                                    maxLength={8}
                                    className="w-28 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20]"
                                    onChange={(e) => setAuthCode(e.target.value)}
                                />
                                <button
                                    onClick={() => handleAuthCodeSubmit(authCode)}
                                    className="px-4 py-2 text-sm font-medium text-white bg-[#BC8B20] rounded-md shadow-sm hover:bg-[#a37517] focus:outline-none"
                                    disabled={authCode.length !== 8}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return <div className='flex font-sans_book_body cursor-default justify-center items-center z-10 fixed left-0 top-0 w-screen h-screen  bg-gray-500 bg-opacity-75 transition-opacity'>
                <div className='relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 mx-4 md:mx-0 w-[50%]'>
                    <div className='w-full flex flex-col '>
                        <div className="mt-3 text-center sm:mt-0 px-5 py-3 sm:ml-4 sm:text-left">
                            <h1 className='text-left py-1 text-lg font-bold leading-6 text-gray-900'>Alert!</h1>
                            <div className="mt-4 ">
                                <p className="text-sm text-gray-500 text-start">{reachedLimitPopup.message}</p>
                            </div>
                        </div>
                    </div>
                    <div className='flex bg-gray-50 px-4 pb-3 flex-row-reverse'>
                        <button onClick={() => setReachedLimitPopup(prev => ({ ...prev, show: false }))} className='inline-flex w-full justify-center font-sans_bold_body rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none  sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        }
    }

    return (
        <div>
            <ConfirmPopup {...alert} onChange={onChange} icon={<></>} loading={loading} activeBtnName={isDownloading ? "Downloading" : "Download"} closeBtnName='Close' title='Select option for pdf file export' disable={isDownloading} />
            {isDownloading
                ? <Spin indicator={<LoadingOutlined style={{ fontSize: 26, color: "#BC8B20", }} spin />} />
                : <Dropdown
                    menu={{
                        items: menuItems.map(item => ({ ...item, onClick: onItemSelect }))
                    }}
                >
                    <button>
                        <DownloadTrigger />
                    </button>
                </Dropdown>
            }
        </div>
    )
}

export default MostActiveAcquirerDownloader